import type { ChangeEvent, RefObject } from 'react';
import { useCallback, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { usePromotePageData, usePromotePageState } from 'src/app/pages/promote/pageContext';
import { useTracking } from 'src/app/pages/promote/shared/hooks';

import { useButtonAmounts } from './hooks/useButtonAmounts';
import { useCustomAmount } from './hooks/useCustomAmount';

type ButtonAmount = {
	value: string;
	label: string;
	valueDisplay: string;
	bonusLabel?: string;
	metaText?: string;
};

type Result = ModelHookResult<
	{
		buttonAmounts: ButtonAmount[];
		selection: string | undefined;
		customAmount: string;
		customInputRef: RefObject<HTMLInputElement | null>;
		amountErrorText: string | null;
		customAmountLabel: string;
		currencySymbol: string;
		currencySymbolPosition: 'before' | 'after';
	},
	{
		handleAmountChange: (value: string) => void;
		handleCustomAmountFocus: () => void;
		handleCustomAmountChange: (value: ChangeEvent<HTMLInputElement>) => void;
	}
>;

// eslint-disable-next-line max-lines-per-function
export function useAmountSelector(): Result {
	const {
		data: {
			amountChoices,
			currency: { code: currencyCode },
		},
	} = usePromotePageData();
	const [{ amount: amountInPageState, validationErrors }, { setAmount, setCustomAmountSelected, setValidationErrors }] =
		usePromotePageState();
	const { translate, getCurrencySymbol, getCurrencySymbolPosition } = useI18n();
	const { trackAmountChanged } = useTracking();
	const [selection, setSelection] = useState<string | undefined>(() => {
		if (!amountInPageState) return undefined;
		if (amountChoices.some((amount) => amount === amountInPageState)) {
			return `${amountInPageState}`;
		}
		return 'custom';
	});

	const buttonAmounts = useButtonAmounts();
	const customAmount = useCustomAmount();

	const handleAmountChange = useCallback(
		(value: string): void => {
			setSelection(value);
			setCustomAmountSelected(value === 'custom');
			if (value !== 'custom') {
				setValidationErrors({ ...validationErrors, amount: '', amountSelection: '', customAmount: '' });
				const valueAsNumber = Number(value);
				const buttonIndex = amountChoices.indexOf(valueAsNumber) + 1;
				setAmount(valueAsNumber);
				trackAmountChanged({ buttonWasClicked: true, buttonClicked: `p${buttonIndex}` });
			} else {
				customAmount.actions.handleCustomAmountSelect();
			}
		},
		[
			amountChoices,
			setAmount,
			setCustomAmountSelected,
			setValidationErrors,
			trackAmountChanged,
			customAmount.actions,
			validationErrors,
		],
	);

	return {
		data: {
			buttonAmounts,
			selection,
			...customAmount.data,
			amountErrorText: validationErrors ? validationErrors.amount : null,
			customAmountLabel: translate('fe.pages.sponsors.other_amount'),
			currencySymbol: getCurrencySymbol(currencyCode),
			currencySymbolPosition: getCurrencySymbolPosition(currencyCode),
		},
		actions: {
			handleAmountChange,
			handleCustomAmountFocus: customAmount.actions.handleCustomAmountFocus,
			handleCustomAmountChange: customAmount.actions.handleCustomAmountChange,
		},
	};
}
